
import { useState, useEffect, useCallback, useRef } from 'react';
import { derivativesApi } from '@/api/derivatives';
import { DerivativeCreation } from '@/data/models/types';
import { useInView } from 'react-intersection-observer';
import { toast } from 'sonner';

interface InfiniteDerivativesOptions {
  initialLimit?: number;
  fetchType: 'latest' | 'popular' | 'playlist' | 'channel' | 'user';
  id?: string; // Required for playlist, channel, or user fetches
  disableAutoLoading?: boolean; // Option to disable auto-loading
  expressionType?: string; // Filter by expression type
}

/**
 * Hook for fetching derivatives with pagination and filtering
 */
export function useInfiniteDerivatives({
  initialLimit = 12, // Changed from 24 to 12
  fetchType,
  id,
  disableAutoLoading = false,
  expressionType = 'all'
}: InfiniteDerivativesOptions) {
  const [derivatives, setDerivatives] = useState<DerivativeCreation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const requestInProgressRef = useRef(false);
  const totalItemsCountRef = useRef<number | null>(null);

  // Set up intersection observer for infinite scrolling
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
    rootMargin: '600px',
  });

  const fetchDerivatives = useCallback(async (currentCursor: string | null = null) => {
    // Prevent multiple simultaneous requests
    if (requestInProgressRef.current) {
      console.log('[useInfiniteDerivatives] Request already in progress, skipping');
      return;
    }

    try {
      requestInProgressRef.current = true;
      const isInitialFetch = currentCursor === null && derivatives.length === 0;
      
      if (isInitialFetch) {
        setLoading(true);
      } else {
        setIsFetchingMore(true);
      }

      console.log(`[useInfiniteDerivatives] Fetching ${fetchType} derivatives with cursor: ${currentCursor || 'initial'}, expressionType: ${expressionType}`);
      
      const pagination = { 
        limit: initialLimit, 
        cursor: currentCursor,
        expressionType: expressionType !== 'all' ? expressionType : undefined
      };
      
      let result;
      
      switch (fetchType) {
        case 'latest':
          result = await derivativesApi.getLatestDerivatives(pagination);
          break;
        case 'popular':
          result = await derivativesApi.getPopularDerivatives(pagination);
          break;
        case 'playlist':
          if (!id) throw new Error('Playlist ID required');
          result = await derivativesApi.getDerivativesForPlaylist(id, pagination);
          break;
        case 'user':
          if (!id) throw new Error('User ID required');
          result = await derivativesApi.getUserDerivatives(id, pagination);
          break;
        default:
          throw new Error(`Unsupported fetch type: ${fetchType}`);
      }
      
      if (!result) {
        throw new Error('Failed to fetch derivatives');
      }
      
      const { data: newItems = [], cursor: newCursor, hasMore: moreAvailable, totalCount } = result;
      
      // Store total count for reference
      if (totalCount !== undefined && isInitialFetch) {
        totalItemsCountRef.current = totalCount;
      }
      
      console.log(`[useInfiniteDerivatives] API response:`, {
        newItemsCount: newItems?.length || 0,
        newCursor,
        moreAvailable,
        totalCount,
        currentItems: derivatives.length,
        expressionType
      });
      
      if (newItems && newItems.length > 0) {
        setDerivatives(prev => isInitialFetch ? newItems : [...prev, ...newItems]);
        setCursor(newCursor);
        setHasMore(!!moreAvailable);
        
        // Double check with total count if available
        if (totalItemsCountRef.current !== null) {
          const willHaveItems = (isInitialFetch ? 0 : derivatives.length) + newItems.length;
          const stillHasMore = willHaveItems < totalItemsCountRef.current;
          console.log(`[useInfiniteDerivatives] Count check: have ${willHaveItems}, total ${totalItemsCountRef.current}, hasMore: ${stillHasMore}`);
          setHasMore(stillHasMore);
        }
      } else {
        console.log('[useInfiniteDerivatives] No new items returned, setting hasMore to false');
        if (isInitialFetch) {
          setDerivatives([]);
        }
        setHasMore(false);
      }
      
      setError(null);
    } catch (err: any) {
      console.error('[useInfiniteDerivatives] Error fetching derivatives:', err);
      setError(err.message || 'Failed to load content');
      toast.error(err.message || 'Failed to load content');
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
      requestInProgressRef.current = false;
    }
  }, [fetchType, id, derivatives.length, initialLimit, expressionType]);

  // Initial load and reload when expression type changes
  useEffect(() => {
    console.log('[useInfiniteDerivatives] Initial fetch or filter change triggered');
    setDerivatives([]);
    setCursor(null);
    setHasMore(true);
    totalItemsCountRef.current = null;
    fetchDerivatives();
  }, [fetchType, id, expressionType]);

  // Load more when scrolling to bottom - only if auto-loading is enabled
  useEffect(() => {
    if (!disableAutoLoading && inView && !loading && !isFetchingMore && hasMore && cursor) {
      console.log('[useInfiniteDerivatives] Scroll threshold reached, loading more items');
      fetchDerivatives(cursor);
    }
  }, [inView, loading, isFetchingMore, hasMore, cursor, fetchDerivatives, disableAutoLoading]);

  // Manual load more function
  const loadMore = useCallback(() => {
    if (isFetchingMore || requestInProgressRef.current || !hasMore) {
      console.log('[useInfiniteDerivatives] Cannot load more:', { 
        isFetchingMore, 
        hasMore, 
        requestInProgress: requestInProgressRef.current 
      });
      return;
    }
    
    console.log('[useInfiniteDerivatives] Manual loadMore called with cursor:', cursor);
    fetchDerivatives(cursor);
  }, [cursor, fetchDerivatives, isFetchingMore, hasMore]);

  return {
    derivatives,
    loading,
    error,
    hasMore,
    isFetchingMore,
    loadMoreRef: ref,
    refetch: () => {
      setDerivatives([]);
      setCursor(null);
      setHasMore(true);
      totalItemsCountRef.current = null;
      fetchDerivatives();
    },
    loadMore
  };
}
